import React, { useState, useRef, useCallback, memo, useEffect } from "react";
import { AlertCircle, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface VideoPlayerProps {
  src: string;
  className?: string;
  controls?: boolean;
  muted?: boolean;
  loop?: boolean;
  autoPlay?: boolean;
  isPaused?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  className = "",
  controls = true,
  muted = false,
  loop = false,
  autoPlay = false,
  isPaused = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleCanPlay = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleWaiting = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handlePlaying = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleRetry = useCallback(() => {
    setHasError(false);
    setIsLoading(true);
    setRetryCount((prev) => prev + 1);
    
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, []);

  // Efeito para pausar/resumir baseado no isPaused
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPaused) {
      video.pause();
    } else if (autoPlay && !hasError && !isLoading) {
      video.play().catch(() => {
        // Silently handle autoplay failures
      });
    }
  }, [isPaused, autoPlay, hasError, isLoading]);

  return (
    <div className="relative w-full h-full">
      {/* Loading Overlay */}
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
            <span className="text-sm text-muted-foreground">Loading video...</span>
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-10 p-4">
          <AlertCircle className="w-12 h-12 mb-4 text-destructive" />
          <p className="text-foreground text-center mb-2 font-medium">
            Failed to load video
          </p>
          <p className="text-muted-foreground text-sm text-center mb-4">
            Please check your connection and try again
          </p>
          <Button 
            onClick={handleRetry} 
            variant="outline" 
            size="sm"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </Button>
        </div>
      )}

      {/* Video Element */}
      <video
        ref={videoRef}
        key={`video-${retryCount}`}
        controls={controls}
        muted={muted}
        loop={loop}
        autoPlay={autoPlay && !isPaused}
        playsInline
        preload="auto"
        className={`w-full h-full ${className}`}
        onCanPlay={handleCanPlay}
        onCanPlayThrough={handleCanPlay}
        onError={handleError}
        onWaiting={handleWaiting}
        onPlaying={handlePlaying}
        onLoadedData={handleCanPlay}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support video playback.
      </video>
    </div>
  );
};

export default memo(VideoPlayer);
